/**
 * HaulerCentral Auth Guard
 *
 * Route guard component for protected routes.
 * Following patterns from @commander/auth.
 */

import type React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useHaulerCentralAuth } from '../hooks/useHaulerCentralAuth';
import { useHasHydrated } from '../stores/authStore';

interface HaulerCentralAuthGuardProps {
  children: React.ReactNode;
  /**
   * Redirect path when not authenticated
   * @default '/login'
   */
  redirectTo?: string;
  /**
   * Allow loading state to show children
   * @default false
   */
  showLoading?: boolean;
  /**
   * Loading component to show while checking auth
   */
  loadingComponent?: React.ReactNode;
}

/**
 * Auth guard component for protected routes
 * Redirects to login if user is not authenticated
 */
export function HaulerCentralAuthGuard({
  children,
  redirectTo = '/login',
  showLoading = false,
  loadingComponent,
}: HaulerCentralAuthGuardProps) {
  const { isAuthenticated, isLoading } = useHaulerCentralAuth();
  const hasHydrated = useHasHydrated();
  const location = useLocation();

  // Show loading while checking auth state
  if (!hasHydrated || isLoading) {
    if (showLoading) {
      return <>{children}</>;
    }
    return <>{loadingComponent || <LoadingSpinner />}</>;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return (
      <Navigate to={redirectTo} state={{ from: location.pathname + location.search }} replace />
    );
  }

  return <>{children}</>;
}

/**
 * Simple loading spinner component
 */
function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
    </div>
  );
}

/**
 * Reverse auth guard - redirect to app if already authenticated
 */
interface GuestGuardProps {
  children: React.ReactNode;
  /**
   * Redirect path when already authenticated
   * @default '/'
   */
  redirectTo?: string;
}

export function GuestGuard({ children, redirectTo = '/' }: GuestGuardProps) {
  const { isAuthenticated, isLoading } = useHaulerCentralAuth();
  const hasHydrated = useHasHydrated();
  const location = useLocation();

  // Show loading while checking auth state
  if (!hasHydrated || isLoading) {
    return <LoadingSpinner />;
  }

  // Redirect to app if already authenticated
  if (isAuthenticated) {
    const from = (location?.state as any)?.from || redirectTo;
    return <Navigate to={from} replace />;
  }

  return <>{children}</>;
}

export default HaulerCentralAuthGuard;
